import { Vector2 } from '../../odie/math/vector';
import { PositionType } from '../../lib/defs/types';
import { arrayIterate2, objectAccess } from '../../lib/util/jsTools';
import { Cell, MapComponent } from '../components/MapComponent';
import { TileComponent } from '../components/TileComponent';
import { BlockId, blockPropsMap } from '../defs/block';
import { config } from '../defs/config';
import { MapDef } from '../defs/map';
import type { BlockEntity } from '../entities/BlockEntity';
import type { GameScene } from '../GameScene';
import { getGameScene } from '../getGameScene';
import { blockIsFrozen, blockViewAssets } from './blockTools';
import { AssetDescription } from '@play-co/timestep-core';

// types
//-----------------------------------------------------------------------------
export type MapStats = {
  [key in BlockId]?: {
    count: number;
  };
};
export type CellIterator = (cell: Cell, position: Vector2) => void;

// constants
//-----------------------------------------------------------------------------
// determines relative vertical position of grid
const alignY = 0.45;

// def
//-----------------------------------------------------------------------------

export function mapGetAssets(
  mapDef: MapDef,
): (string | AssetDescription<any>)[] {
  // collect block assets
  const blockAssets: (string | AssetDescription<any>)[] = [];
  arrayIterate2(mapDef.grid.cells, (cell) =>
    cell.blocks?.forEach((block) =>
      blockAssets.push(...blockViewAssets(block.id)),
    ),
  );

  return [
    'symbols/',
    ...TileComponent.assets(),
    ...blockAssets,
    ...mapDef.spawns.map((id) => blockViewAssets(id)).flat(),
  ];
}

export function mapGetStats(mapDef: MapDef, onlyGoals = false): MapStats {
  const stats: MapStats = {};
  arrayIterate2(mapDef.grid.cells, (cell) =>
    cell.blocks?.forEach((block) => {
      if (!onlyGoals) {
        // Count each block type as always
        objectAccess(block.id, stats, { count: 0 }).count += 1;
        return;
      }

      // If only goals, custom goals should count as their base block
      const props = blockPropsMap[block.id];
      const goal = props.sharedGoal ?? props.customGoal ?? block.id;
      const count = props.goalCountMultiplier ?? 1;
      objectAccess(goal, stats, { count: 0 }).count += count;
    }),
  );
  return stats;
}

export function mapGetPan(position: PositionType) {
  return (position.x + 0.5 - config.map.columns / 2) / config.map.columns;
}

// position
//-----------------------------------------------------------------------------
/**
 * Convert a map coordinate into the view coordinate space of the visible grid.
 *
 * @param position Map coordinate to convert.
 * @param centered Whether to return the center instead of the corner of the tile.
 * @returns Position in the Match2 view coordinate space.
 */
export function mapToViewPosition(
  position: PositionType,
  centered?: boolean,
): Vector2 {
  const gameScene = getGameScene();

  // If scene not found, return position as is
  if (!gameScene) {
    return new Vector2(position.x, position.y);
  }

  const map = gameScene.sessionEntity.c.map;
  const tileSize = config.tile.size;
  const center = centered ? tileSize / 2 : 0;

  // convert map to view position
  return new Vector2(
    position.x * tileSize -
      (tileSize * (config.map.columns + map.columnOffset)) / 2 +
      center,
    position.y * tileSize - tileSize * map.visibleRows * alignY + center,
  );
}

/**
 * Convert a view coordinate into the map coordinate space of the visible grid.
 *
 * @param position Match2 view coordinate to convert.
 * @returns Tile coordinate in the active map.
 */
export function mapFromViewPosition(position: PositionType): Vector2 {
  const gameScene = getGameScene();

  // If scene not found, return position as is
  if (!gameScene) {
    return new Vector2(position.x, position.y);
  }

  const map = gameScene.sessionEntity.c.map;
  const tileSize = config.tile.size;

  // convert view to map position
  return new Vector2(
    Math.floor(
      (position.x +
        (tileSize * (config.map.columns + map.columnOffset) + tileSize) / 2) /
        tileSize,
    ),
    Math.floor(
      (position.y + (tileSize * map.visibleRows + tileSize) * alignY) /
        tileSize,
    ),
  );
}

export function mapFromGlobalPosition(position: PositionType): PositionType {
  const gameScene = getGameScene();

  // If scene not found, return position as is
  if (!gameScene) {
    return position;
  }

  const map = gameScene.view2d.layers.default;
  const local = map.toLocal(position);
  const offset = config.tile.size / 2;
  return mapFromViewPosition({
    x: local.x - offset,
    y: local.y - offset,
  });
}

export function mapIsPositionNextTo(
  position1: Vector2,
  position2: Vector2,
): boolean {
  const dx = Math.abs(position1.x - position2.x);
  const dy = Math.abs(position1.y - position2.y);

  return dx <= 1 && dy <= 1 && dx !== dy;
}

// cell
//-----------------------------------------------------------------------------
export function mapCellBaseOpen(cell: Cell): boolean {
  return !cell.base && !cell.overlay?.entity.c.block.props.freeze;
}

export function isCellBarrier(cell: Cell) {
  // barrier if base block or overlay is a barrier
  return cell.base || cell.overlay?.entity.c.block.props.barrier;
}

// fall
//-----------------------------------------------------------------------------
export function isDroppableCell(scene: GameScene, cell: Cell): boolean {
  if (!cell?.base || cell.base.parent) {
    return false;
  }

  const entity = cell.base.entity;
  const block = entity.c.block;

  return !block.props.immobile && !blockIsFrozen(scene, entity);
}

export function findDropRow(
  map: MapComponent,
  entity: BlockEntity,
): number | undefined {
  const position = entity.c.position.mapPosition;
  let minDropRow = 1000;

  // for each column of the block component
  for (let blockColumn = 0; blockColumn < entity.c.block.width; ++blockColumn) {
    const dropRow = findColumnDropRow(
      map,
      entity,
      position.x + blockColumn,
      position.y + 1,
    );
    if (dropRow === undefined) return undefined;
    minDropRow = Math.min(dropRow, minDropRow);
  }

  return minDropRow;
}

export function findColumnDropRow(
  map: MapComponent,
  entity: BlockEntity,
  column: number,
  row: number,
): number | undefined {
  const columns = map.grid[column];
  const height = entity.c.block.height;
  let dropRow;
  let gapRows = 0;

  // for each row in the cell column from the given row
  for (; row < columns.length; ++row) {
    const cell = columns[row];

    // if cell is enabled and does not have an entity, update drop row
    if (cell.enabled) {
      // stop at first barrier that isnt given entity
      if (isCellBarrier(cell) && entity !== cell.base?.entity) {
        break;
      }

      // Stop if cell has overlay and entity is also overlay, so that it doesn't fall through
      if (cell.overlay && entity.c.block.props.overlay) {
        break;
      }

      // if enough gap rows, update drop row
      if (++gapRows >= height) {
        dropRow = row - height + 1;
      }
    } else {
      gapRows = 0;
    }
  }

  return dropRow;
}

export function hasBlockBelow(map: MapComponent, entity: BlockEntity): boolean {
  const block = entity.c.block;
  const height = block.props.height || 1;

  const position = entity.c.position.mapPosition;
  const fromColumn = position.x;
  const fromRow = position.y + height;

  // for each column of block's width
  for (let column = 0; column < block.width; ++column) {
    const columns = map.grid[column + fromColumn];

    // for each row below this block
    for (let row = fromRow; row < columns.length; ++row) {
      const cell = columns[row];
      if (cell?.enabled && cell.base) {
        return true;
      }
    }
  }

  return false;
}

// iteration
//-----------------------------------------------------------------------------
export function mapIterateCells(scene: GameScene, handler: CellIterator) {
  const map = scene.sessionEntity.c.map;

  // for each cell in map
  for (let column = 0; column < map.columns; ++column) {
    for (let row = 0; row < map.rows; ++row) {
      const cell = map.grid[column][row];
      if (cell.enabled) handler(cell, new Vector2(column, row));
    }
  }
}
